import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Public base shown to end-users in code samples / docs (their OpenAI client
// base_url). Falls back to the current origin so preview/prod both work.
const PUBLIC_BASE = process.env.REACT_APP_PUBLIC_BASE_URL
  || (typeof window !== 'undefined' ? window.location.origin : '');
export { PUBLIC_BASE };
export const GATEWAY_BASE = `${PUBLIC_BASE}/api/v1`;

const client = axios.create({ baseURL: API_BASE });

client.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

client.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401 && localStorage.getItem('token')) {
      // token invalid/expired
      localStorage.removeItem('token');
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

export const errMsg = (e, fallback = 'حدث خطأ، حاول مرة أخرى') =>
  e?.response?.data?.detail || e?.response?.data?.error?.message || e?.message || fallback;

export const api = {
  // auth
  register: (data) => client.post('/auth/register', data),
  login: (data) => client.post('/auth/login', data),
  googleSession: (data) => client.post('/auth/google/session', data),
  me: () => client.get('/auth/me'),
  // password reset (forgot password via Telegram/WhatsApp approval)
  resetRequest: (data) => client.post('/password-reset/request', data),
  resetStatus: (id) => client.get(`/password-reset/status/${id}`),
  resetComplete: (data) => client.post('/password-reset/complete', data),
  // blog (public + admin CMS)
  blogPosts: (params) => client.get('/blog/posts', { params }),
  blogPost: (slug) => client.get(`/blog/posts/${slug}`),
  adminBlogPosts: () => client.get('/admin/blog/posts'),
  adminBlogPost: (id) => client.get(`/admin/blog/posts/${id}`),
  adminCreateBlogPost: (data) => client.post('/admin/blog/posts', data),
  adminUpdateBlogPost: (id, data) => client.put(`/admin/blog/posts/${id}`, data),
  adminDeleteBlogPost: (id) => client.delete(`/admin/blog/posts/${id}`),
  // public
  publicSettings: () => client.get('/settings/public'),
  chatbotSend: (data) => client.post('/chatbot/message', data),
  models: () => client.get('/models'),
  contactMethods: () => client.get('/contact-methods'),
  // user
  dashboard: () => client.get('/dashboard'),
  keys: () => client.get('/keys'),
  createKey: (data) => client.post('/keys', data),
  updateKey: (id, data) => client.patch(`/keys/${id}`, data),
  revokeKey: (id) => client.delete(`/keys/${id}`),
  usage: (params) => client.get('/usage', { params }),
  transactions: (params) => client.get('/transactions', { params }),
  activity: (params) => client.get('/activity', { params }),
  // deposits (user)
  depositMethods: () => client.get('/deposit-methods'),
  createDeposit: (data) => client.post('/deposits', data),
  myDeposits: () => client.get('/deposits'),
  estimate: (data) => client.post('/tools/estimate', data),
  referral: () => client.get('/referral'),
  redeemPromo: (data) => client.post('/promo/redeem', data),
  // account activation (manual verification via WhatsApp/Telegram)
  activationRequest: (data) => client.post('/activation/request', data),
  activationStatus: () => client.get('/activation/status'),
  // playground
  pgChat: (data) => client.post('/playground/chat', data),
  pgImage: (data) => client.post('/playground/image', data),
  pgVideo: (data) => client.post('/playground/video', data),
  pgVideoStatus: (id) => client.get(`/playground/video/${id}`),
  // public verify (proof) — authenticated. Chat uses /playground/chat (same
  // billing & flow as the playground itself).
  verifyModels: () => client.get('/verify/models'),
  verifyFunctionCallPreset: () => client.get('/verify/function-call/preset'),
  verifyFunctionCall: (data) => client.post('/verify/function-call', data),
  // admin
  adminStats: () => client.get('/admin/stats'),
  adminUsers: (params) => client.get('/admin/users', { params }),
  adminUserDetail: (id) => client.get(`/admin/users/${id}`),
  adminCredit: (id, data) => client.post(`/admin/users/${id}/credit`, data),
  adminUpdateUser: (id, data) => client.patch(`/admin/users/${id}`, data),
  adminDeleteUser: (id) => client.delete(`/admin/users/${id}`),
  adminGenerateResetLink: (id) => client.post(`/admin/users/${id}/password-reset-link`),
  adminModels: () => client.get('/admin/models'),
  adminOfficialPrices: (rate) => client.get('/admin/models/official', { params: rate ? { usd_to_egp: rate } : {} }),
  adminSyncOfficialPrices: (data) => client.post('/admin/models/sync-official', data),
  adminCreateModel: (data) => client.post('/admin/models', data),
  adminUpdateModel: (id, data) => client.patch(`/admin/models/${id}`, data),
  adminDeleteModel: (id) => client.delete(`/admin/models/${id}`),
  adminUsage: (params) => client.get('/admin/usage', { params }),
  adminClearUsageLogs: () => client.post('/admin/maintenance/clear-usage-logs'),
  adminTransactions: (params) => client.get('/admin/transactions', { params }),
  adminActivity: (params) => client.get('/admin/activity', { params }),
  adminSettings: () => client.get('/admin/settings'),
  adminUpdateSettings: (data) => client.patch('/admin/settings', data),
  // admin LLM key override
  adminLlmKey: () => client.get('/admin/llm-key'),
  adminSetLlmKey: (data) => client.post('/admin/llm-key', data),
  adminTestLlmKey: (data) => client.post('/admin/llm-key/test', data || {}),
  adminDeleteLlmKey: () => client.delete('/admin/llm-key'),
  adminReactivateLlmKey: () => client.post('/admin/llm-key/reactivate'),
  // admin deposits
  adminDepositMethods: () => client.get('/admin/deposit-methods'),
  adminCreateDepositMethod: (data) => client.post('/admin/deposit-methods', data),
  adminUpdateDepositMethod: (id, data) => client.patch(`/admin/deposit-methods/${id}`, data),
  adminDeleteDepositMethod: (id) => client.delete(`/admin/deposit-methods/${id}`),
  adminDeposits: (params) => client.get('/admin/deposits', { params }),
  adminApproveDeposit: (id) => client.post(`/admin/deposits/${id}/approve`),
  adminRejectDeposit: (id, data) => client.post(`/admin/deposits/${id}/reject`, data),
  // admin promo codes
  adminPromoCodes: () => client.get('/admin/promo-codes'),
  adminPromoRedemptions: (id) => client.get(`/admin/promo-codes/${id}/redemptions`),
  adminCreatePromoCode: (data) => client.post('/admin/promo-codes', data),
  adminUpdatePromoCode: (id, data) => client.patch(`/admin/promo-codes/${id}`, data),
  adminDeletePromoCode: (id) => client.delete(`/admin/promo-codes/${id}`),
  // admin contact methods
  adminContactMethods: () => client.get('/admin/contact-methods'),
  adminCreateContactMethod: (data) => client.post('/admin/contact-methods', data),
  adminUpdateContactMethod: (id, data) => client.patch(`/admin/contact-methods/${id}`, data),
  adminDeleteContactMethod: (id) => client.delete(`/admin/contact-methods/${id}`),
  // admin activations (manual verification) + soft-deleted accounts
  adminActivations: (params) => client.get('/admin/activations', { params }),
  adminApproveActivation: (userId) => client.post(`/admin/activations/${userId}/approve`),
  adminRejectActivation: (userId) => client.post(`/admin/activations/${userId}/reject`),
  adminDeletedUsers: () => client.get('/admin/deleted-users'),
  adminRestoreUser: (id) => client.post(`/admin/users/${id}/restore`),
  // admin security events (audit log + telegram alerts)
  adminSecurityEvents: (params) => client.get('/admin/security-events', { params }),
  adminClearSecurityEvents: () => client.delete('/admin/security-events'),
  // database backup / restore (root admin)
  adminBackupSummary: () => client.get('/admin/backup/summary'),
  adminBackupExport: () => client.get('/admin/backup/export', { responseType: 'blob', timeout: 0 }),
  adminBackupRestore: (formData, onUploadProgress) => client.post('/admin/backup/restore', formData, { onUploadProgress, timeout: 0 }),
  adminBackupRestoreStatus: (jobId) => axios.get(`${API_BASE}/admin/backup/restore/${jobId}`),
  adminTestSecurityAlert: () => client.post('/admin/security-events/test-alert'),
  // telegram bot setup / status
  telegramSetup: (data) => client.post('/telegram/setup', data),
  telegramStatus: () => client.get('/telegram/status'),
  // admin chatbot (site-support bot) monitoring
  adminChatbotStats: () => client.get('/admin/chatbot/stats'),
  adminChatbotTopIps: (params) => client.get('/admin/chatbot/top-ips', { params }),
  adminChatbotRecent: (params) => client.get('/admin/chatbot/recent', { params }),
  adminChatbotBlockedIps: () => client.get('/admin/chatbot/blocked-ips'),
  adminChatbotBlockIp: (data) => client.post('/admin/chatbot/block-ip', data),
  adminChatbotUnblockIp: (ip) => client.delete(`/admin/chatbot/block-ip/${encodeURIComponent(ip)}`),
  adminChatbotSetThreshold: (data) => client.patch('/admin/chatbot/settings', data),
  adminChatbotDeleteSession: (sessionId) => client.delete(`/admin/chatbot/sessions/${encodeURIComponent(sessionId)}`),
  adminChatbotDeleteAll: (scope) => client.delete('/admin/chatbot/sessions', { params: scope ? { scope } : undefined }),
  // admin — studio plans (BYOK + subscription)
  adminStudioListPlans: () => client.get('/admin/studio/plans'),
  adminStudioUpdatePlan: (id, data) => client.patch(`/admin/studio/plans/${id}`, data),
  adminStudioResetPlans: () => client.post('/admin/studio/plans/reset'),
  adminStudioUserSub: (userId) => client.get(`/admin/studio/users/${userId}/subscription`),
  adminStudioGrantPlan: (userId, data) => client.post(`/admin/studio/users/${userId}/grant`, data),
  adminStudioRevokeSub: (userId) => client.delete(`/admin/studio/users/${userId}/subscription`),
};

export default client;
